import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { B, space, font, radius, weight } from '../constants/brand';
import { api } from '../api/client';
import { fmt, displayTimeZone } from '../utils/format';
import {
  Btn, Card, Modal, ModalFooter, Input, Textarea, Select, PageHeader,
  Spinner, EmptyState, Chip, Icon, useToast, Tabs,
} from '../components/ui';
import { usePersonalConnectStatus, NotConnectedCard } from '../components/shared/PersonalConnectGate';
import {
  isoDate, addDays, monthGrid, viewRange, stepAnchor, dayPlacement, layoutOverlaps, REMINDER_CHOICES,
} from '../utils/calendarGrid';

// ── Calendar — the in-app Outlook calendar ──────────────────────────────────
// Reads, creates, edits and deletes events on the SIGNED-IN PERSON'S OWN
// Outlook calendars, through the same delegated Microsoft connection as Mail
// and Harvi's calendar tool. A direct, human-driven surface — see the header
// comment in pages/Mail.jsx for why that means no propose/review step here.
//
// GRAPH'S OWN id SEMANTICS DECIDE "edit this occurrence" vs "edit the whole
// series" — see api/src/functions/calendar.js. Every event card carries
// whatever id calendarView gave it, and a repeating event's card offers
// "Edit the whole series" which re-opens the SAME modal on seriesMasterId.
//
// Views: Day · Work week · Week · Month · Agenda. Several calendars at once,
// each in its own colour, each toggleable. The Scheduling Assistant reads
// free/busy (getSchedule) and suggests common free times.
//
// NOT BUILT, stated rather than silently missing: drag-to-move/resize an
// event, un-recurring an existing series, relative recurrences ("third
// Tuesday"), a shared mailbox's calendar on this page (the API supports it).

// Events are fetched and created in the person's SAVED zone (Graph returns
// wall-clock times in whatever zone we ask for). Read at call time — the
// profile sets it after this module loads.
const tzNow = () => displayTimeZone();

const pad2 = (n) => String(n).padStart(2, '0');
const isoDateTimeMidnight = (d) => `${isoDate(d)}T00:00:00`;

// A Graph dateTimeTimeZone's `dateTime` is a WALL-CLOCK string with NO zone
// suffix — parsing it with `new Date(str)` would reinterpret it in the
// browser's own zone. The digits are read directly instead.
function parseWallClock(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s || '');
  if (!m) return null;
  return { year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5] };
}
function hhmm12(hour, minute) {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${pad2(minute)} ${hour < 12 ? 'AM' : 'PM'}`;
}
function time12h(when) {
  const p = when && when.dateTime ? parseWallClock(when.dateTime) : null;
  return p ? hhmm12(p.hour, p.minute) : '';
}
function localTime12(local) {
  const p = parseWallClock(local);
  return p ? hhmm12(p.hour, p.minute) : '';
}
function dayKeyOf(when) {
  return when && when.dateTime ? when.dateTime.slice(0, 10) : null;
}
/** Every day key an event covers — an all-day or multi-day event shows on each. */
function dayKeysOf(e) {
  const s = dayKeyOf(e.start);
  if (!s) return [];
  let endKey = dayKeyOf(e.end) || s;
  // Graph's all-day end is EXCLUSIVE; a timed event ending exactly at
  // midnight does not occupy the next day either.
  if (e.isAllDay || (e.end && /T00:00/.test(e.end.dateTime || ''))) {
    const [y, m, d] = endKey.split('-').map(Number);
    endKey = isoDate(addDays(new Date(y, m - 1, d), -1));
  }
  if (endKey < s) endKey = s;
  const out = [];
  const [y, m, d] = s.split('-').map(Number);
  for (let cur = new Date(y, m - 1, d), n = 0; isoDate(cur) <= endKey && n < 62; cur = addDays(cur, 1), n += 1) out.push(isoDate(cur));
  return out;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DEFAULT_COLOR = '#2b7a8c';

function addressLine(list) {
  return (list || []).map((a) => (a.name && a.name !== a.address ? `${a.name} <${a.address}>` : a.address)).join(', ');
}

const SHOW_AS_OPTIONS = [
  { value: 'busy', label: 'Busy' }, { value: 'tentative', label: 'Tentative' }, { value: 'free', label: 'Free' },
  { value: 'oof', label: 'Away' }, { value: 'workingElsewhere', label: 'Working elsewhere' },
];
const RESPONSE_LABEL = { accepted: 'Accepted', tentativelyAccepted: 'Tentative', declined: 'Declined', notResponded: 'No response', none: 'No response', organizer: 'Organizer' };

// Visible-calendar choices are remembered per browser; a blocked storage
// read degrades to "show every calendar".
const CAL_PREFS_KEY = 'harvus.calendar.prefs';
function loadCalPrefs() {
  try { const v = JSON.parse(window.localStorage.getItem(CAL_PREFS_KEY) || '{}'); return { hidden: Array.isArray(v.hidden) ? v.hidden : [], view: v.view || 'week' }; }
  catch { return { hidden: [], view: 'week' }; }
}
function saveCalPrefs(p) { try { window.localStorage.setItem(CAL_PREFS_KEY, JSON.stringify(p)); } catch { /* best-effort */ } }

// ── Event pill (month + all-day rows) ───────────────────────────────────────

const pillBg = (e) => (e.isCancelled || e.myResponse === 'declined' ? B.surfaceMuted : `${e.color || DEFAULT_COLOR}22`);

const EventPill = ({ e, onClick }) => (
  <button onClick={(ev) => { ev.stopPropagation(); onClick(); }} title={e.subject}
    style={{
      display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
      background: pillBg(e), borderLeft: `3px solid ${e.color || DEFAULT_COLOR}`,
      color: e.isCancelled ? B.textLt : B.navyDk,
      textDecoration: e.isCancelled ? 'line-through' : 'none',
      fontStyle: e.showAs === 'tentative' || e.myResponse === 'tentativelyAccepted' ? 'italic' : 'normal',
      borderRadius: radius.sm, padding: '2px 6px', fontSize: 11, marginBottom: 2,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'inherit',
    }}>
    {!e.isAllDay && <strong>{time12h(e.start)} </strong>}
    {e.sensitivity === 'private' && <Icon name="lock" size={9} style={{ marginRight: 3, verticalAlign: -1 }} />}
    {e.subject}
    {e.isOnlineMeeting && <Icon name="video" size={10} style={{ marginLeft: 4, verticalAlign: -1 }} />}
  </button>
);

// ── Time grid (Day / Work week / Week) ──────────────────────────────────────
// Hours down the side, one column per day, events placed by their wall-clock
// minutes and laid side by side where they overlap (layoutOverlaps). Clicking
// an empty half hour starts a new event there. Opens scrolled to 7 AM.

const HOUR_PX = 44;

const TimeGrid = ({ days, events, onOpen, onNewAt }) => {
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = HOUR_PX * 7; }, [days.length]);
  const todayKey = isoDate(new Date());
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const perDay = useMemo(() => {
    const m = new Map(days.map((d) => [isoDate(d), { allDay: [], timed: [] }]));
    for (const e of events) {
      if (e.isAllDay) {
        for (const k of dayKeysOf(e)) if (m.has(k)) m.get(k).allDay.push(e);
        continue;
      }
      for (const k of dayKeysOf(e)) {
        if (!m.has(k)) continue;
        const pl = dayPlacement(e.start?.dateTime, e.end?.dateTime, k);
        if (pl) m.get(k).timed.push({ ...pl, e });
      }
    }
    for (const v of m.values()) v.timed = layoutOverlaps(v.timed);
    return m;
  }, [days, events]);

  const cols = `56px repeat(${days.length}, 1fr)`;
  const hasAllDay = [...perDay.values()].some((v) => v.allDay.length);

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: `1px solid ${B.divider}` }}>
        <div />
        {days.map((d) => {
          const key = isoDate(d);
          const isToday = key === todayKey;
          return (
            <div key={key} style={{ padding: '6px 8px', borderLeft: `1px solid ${B.divider}`, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: B.textLt, textTransform: 'uppercase', fontWeight: weight.semibold }}>{WEEKDAY_LABELS[(d.getDay() + 6) % 7]}</div>
              <div style={{
                fontSize: font.base, fontWeight: isToday ? weight.bold : weight.medium, color: isToday ? '#fff' : B.text,
                background: isToday ? B.navy : 'transparent', borderRadius: radius.pill, display: 'inline-block', minWidth: 28, padding: '1px 6px',
              }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      {hasAllDay && (
        <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: `1px solid ${B.divider}`, background: B.surfaceMuted }}>
          <div style={{ fontSize: 10, color: B.textLt, padding: '4px 6px' }}>All day</div>
          {days.map((d) => (
            <div key={isoDate(d)} style={{ borderLeft: `1px solid ${B.divider}`, padding: 3 }}>
              {perDay.get(isoDate(d)).allDay.map((e) => <EventPill key={`${e.id}-${isoDate(d)}`} e={e} onClick={() => onOpen(e)} />)}
            </div>
          ))}
        </div>
      )}
      <div ref={scrollRef} style={{ maxHeight: 'calc(100vh - 330px)', minHeight: 360, overflowY: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: cols, position: 'relative' }}>
          <div>
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{ height: HOUR_PX, fontSize: 10, color: B.textLt, textAlign: 'right', paddingRight: 6, transform: 'translateY(-6px)' }}>
                {h === 0 ? '' : hhmm12(h, 0).replace(':00', '')}
              </div>
            ))}
          </div>
          {days.map((d) => {
            const key = isoDate(d);
            const nowMins = now.getHours() * 60 + now.getMinutes();
            return (
              <div key={key} style={{ position: 'relative', borderLeft: `1px solid ${B.divider}`, height: HOUR_PX * 24 }}>
                {Array.from({ length: 48 }, (_, i) => (
                  <div key={i} role="button" tabIndex={-1} aria-label={`New event at ${hhmm12(Math.floor(i / 2), (i % 2) * 30)}`}
                    onClick={() => onNewAt(d, Math.floor(i / 2), (i % 2) * 30)}
                    style={{ height: HOUR_PX / 2, borderTop: `1px ${i % 2 ? 'dotted' : 'solid'} ${B.divider}`, cursor: 'pointer' }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = B.hover; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }} />
                ))}
                {key === todayKey && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: (nowMins / 60) * HOUR_PX, borderTop: `2px solid ${B.red}`, pointerEvents: 'none', zIndex: 2 }} />
                )}
                {perDay.get(key).timed.map(({ e, top, height, col, cols: n }) => (
                  <button key={`${e.id}-${key}`} onClick={() => onOpen(e)} title={`${e.subject}${e.location ? ` — ${e.location}` : ''}`}
                    style={{
                      position: 'absolute', top: (top / 60) * HOUR_PX + 1, height: Math.max(16, (height / 60) * HOUR_PX - 2),
                      left: `calc(${(100 / n) * col}% + 2px)`, width: `calc(${100 / n}% - 4px)`, zIndex: 1,
                      background: pillBg(e), border: 'none',
                      borderRadius: radius.sm, padding: '2px 5px', textAlign: 'left', cursor: 'pointer', overflow: 'hidden',
                      fontFamily: 'inherit', fontSize: 11, color: e.isCancelled ? B.textLt : B.navyDk,
                      textDecoration: e.isCancelled ? 'line-through' : 'none',
                      fontStyle: e.showAs === 'tentative' || e.myResponse === 'tentativelyAccepted' ? 'italic' : 'normal',
                      boxShadow: `inset 3px 0 0 ${e.color || DEFAULT_COLOR}`,
                    }}>
                    <div style={{ fontWeight: weight.semibold, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.sensitivity === 'private' && <Icon name="lock" size={9} style={{ marginRight: 3, verticalAlign: -1 }} />}
                      {e.subject}
                    </div>
                    {height >= 45 && <div style={{ opacity: 0.8 }}>{time12h(e.start)} – {time12h(e.end)}</div>}
                    {height >= 70 && e.location && <div style={{ opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.location}</div>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

// ── Month view ───────────────────────────────────────────────────────────────

const MonthView = ({ anchor, events, onOpen, onNewOn, onDrill }) => {
  const grid = useMemo(() => monthGrid(anchor), [anchor]);
  const byDay = useMemo(() => {
    const m = new Map(grid.days.map((d) => [d.key, []]));
    for (const e of events) for (const k of dayKeysOf(e)) if (m.has(k)) m.get(k).push(e);
    for (const list of m.values()) list.sort((a, b) => (a.isAllDay === b.isAllDay ? String(a.start?.dateTime).localeCompare(String(b.start?.dateTime)) : a.isAllDay ? -1 : 1));
    return m;
  }, [grid, events]);
  const today = isoDate(new Date());
  const MAX = 3;
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${B.divider}` }}>
        {WEEKDAY_LABELS.map((l) => <div key={l} style={{ padding: '6px 8px', fontSize: 10, fontWeight: weight.semibold, color: B.textLt, textTransform: 'uppercase' }}>{l}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: 'minmax(104px, 1fr)' }}>
        {grid.days.map((d) => {
          const list = byDay.get(d.key) || [];
          return (
            <div key={d.key} onClick={() => onNewOn(d.date)} role="button" tabIndex={-1}
              style={{ borderRight: `1px solid ${B.divider}`, borderBottom: `1px solid ${B.divider}`, padding: 4, cursor: 'pointer', background: d.inMonth ? B.surface : B.surfaceMuted, minWidth: 0 }}>
              <button onClick={(ev) => { ev.stopPropagation(); onDrill(d.date); }}
                style={{
                  border: 'none', background: d.key === today ? B.navy : 'transparent', color: d.key === today ? '#fff' : (d.inMonth ? B.text : B.textLt),
                  borderRadius: radius.pill, fontSize: font.xs, fontWeight: weight.semibold, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 2,
                }}>{d.date.getDate()}</button>
              {list.slice(0, MAX).map((e) => <EventPill key={`${e.id}-${d.key}`} e={e} onClick={() => onOpen(e)} />)}
              {list.length > MAX && (
                <button onClick={(ev) => { ev.stopPropagation(); onDrill(d.date); }}
                  style={{ border: 'none', background: 'none', color: B.navy, fontSize: 11, cursor: 'pointer', padding: '0 4px', fontFamily: 'inherit' }}>
                  +{list.length - MAX} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ── Agenda view ──────────────────────────────────────────────────────────────

const AgendaView = ({ events, onOpen }) => {
  const groups = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      const key = dayKeyOf(e.start);
      if (!key) continue;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(e);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  if (!groups.length) return <EmptyState icon="calendar" title="Nothing scheduled" message="No events in this window." />;

  return (
    <div>
      {groups.map(([day, list]) => (
        <div key={day} style={{ marginBottom: space.lg }}>
          <div style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.textMd, marginBottom: 6 }}>{fmt.date(day)}</div>
          <Card style={{ padding: 0 }}>
            {list.map((e, i) => (
              <div key={e.id} onClick={() => onOpen(e)} role="button" tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === 'Enter') onOpen(e); }}
                style={{ padding: '10px 14px', borderTop: i ? `1px solid ${B.divider}` : 'none', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', borderLeft: `3px solid ${e.color || DEFAULT_COLOR}` }}
                onMouseEnter={(ev) => { ev.currentTarget.style.background = B.hover; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}>
                <div style={{ width: 120, flexShrink: 0, fontSize: font.sm, color: B.textMd }}>
                  {e.isAllDay ? 'All day' : `${time12h(e.start)} – ${time12h(e.end)}`}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: e.isCancelled ? B.textLt : B.text, textDecoration: e.isCancelled ? 'line-through' : 'none' }}>
                    {e.sensitivity === 'private' && <Icon name="lock" size={11} style={{ marginRight: 4, verticalAlign: -1 }} />}
                    {e.subject}
                    {e.isOnlineMeeting && <Icon name="video" size={12} style={{ marginLeft: 6, verticalAlign: -1, opacity: 0.6 }} />}
                  </div>
                  {e.location && <div style={{ fontSize: font.xs, color: B.textLt }}>{e.location}</div>}
                </div>
                {e.joinUrl && (
                  <Btn size="sm" variant="ghost" onClick={(ev) => { ev.stopPropagation(); window.open(e.joinUrl, '_blank', 'noopener'); }}>Join</Btn>
                )}
                {e.myResponse && e.myResponse !== 'organizer' && e.myResponse !== 'none' && (
                  <Chip color={e.myResponse === 'declined' ? B.red : e.myResponse === 'tentativelyAccepted' ? B.amber : B.green}>{RESPONSE_LABEL[e.myResponse] || e.myResponse}</Chip>
                )}
              </div>
            ))}
          </Card>
        </div>
      ))}
    </div>
  );
};

// ── Scheduling assistant ─────────────────────────────────────────────────────
// Free/busy for everyone on the invite (you included) across the chosen day,
// plus suggested common free times over the next week. A person whose
// free/busy Outlook could not read (an outside address, sharing turned off)
// is SAID to be unknown — never drawn as a free day.

const AVAIL_COLOR = { 0: 'transparent', 1: '#c9a227', 2: '#2b5f8c', 3: '#8c2b5f', 4: '#6b7d86' };
const AVAIL_LABEL = { 0: 'Free', 1: 'Tentative', 2: 'Busy', 3: 'Away', 4: 'Working elsewhere' };

const SchedulingAssistant = ({ attendees, date, durationMinutes, onPick }) => {
  const { toast } = useToast();
  const [grid, setGrid] = useState(null);
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const people = attendees.split(',').map((a) => a.trim()).filter(Boolean);

  const run = async () => {
    setLoading(true);
    try {
      const [y, m, d] = date.split('-').map(Number);
      const day = new Date(y, m - 1, d);
      const [g, f] = await Promise.all([
        api.post('calendar/schedule', {
          attendees: people, start: `${date}T00:00`, end: `${isoDate(addDays(day, 1))}T00:00`, timeZone: tzNow(), intervalMinutes: 30,
        }),
        api.post('calendar/find-times', {
          attendees: people, start: `${date}T00:00`, end: `${isoDate(addDays(day, 7))}T00:00`, timeZone: tzNow(), durationMinutes,
        }),
      ]);
      setGrid(g);
      setSlots(f);
    } catch (e) { toast.error(e.message || 'Could not read free/busy.'); }
    finally { setLoading(false); }
  };

  const HOURS = [7, 19]; // the strip draws 7 AM – 7 PM
  const cells = (HOURS[1] - HOURS[0]) * 2;
  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: space.md, marginBottom: space.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: space.sm }}>
        <strong style={{ fontSize: font.sm }}>Scheduling assistant</strong>
        <span style={{ fontSize: font.xs, color: B.textLt, flex: 1 }}>
          {people.length ? `${people.length + 1} people incl. you · ${durationMinutes} min` : 'Add attendees above, or check just your own calendar'}
        </span>
        <Btn size="sm" variant="ghost" onClick={run} loading={loading}><Icon name="search" size={12} /> Check availability</Btn>
      </div>
      {grid && (
        <div style={{ marginBottom: space.md }}>
          <div style={{ display: 'grid', gridTemplateColumns: `160px repeat(${cells}, 1fr)`, fontSize: 9, color: B.textLt }}>
            <div />
            {Array.from({ length: cells }, (_, i) => <div key={i}>{i % 2 === 0 ? hhmm12(HOURS[0] + i / 2, 0).replace(':00', '').replace(' ', '') : ''}</div>)}
          </div>
          {grid.schedules.map((sch) => (
            <div key={sch.email} style={{ display: 'grid', gridTemplateColumns: `160px repeat(${cells}, 1fr)`, alignItems: 'center', marginTop: 3 }}>
              <div style={{ fontSize: font.xs, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 6 }} title={sch.email}>{sch.email}</div>
              {sch.error ? (
                <div style={{ gridColumn: `span ${cells}`, fontSize: font.xs, color: B.amber }}>Free/busy not available — {sch.error}</div>
              ) : Array.from({ length: cells }, (_, i) => {
                const c = sch.availabilityView[HOURS[0] * 2 + i] || '0';
                return <div key={i} title={AVAIL_LABEL[c]} style={{ height: 14, background: AVAIL_COLOR[c] || AVAIL_COLOR[2], borderLeft: `1px solid ${B.divider}`, borderTop: `1px solid ${B.divider}`, borderBottom: `1px solid ${B.divider}` }} />;
              })}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 6, fontSize: 10, color: B.textMd }}>
            {[1, 2, 3, 4].map((k) => <span key={k}><span style={{ display: 'inline-block', width: 10, height: 10, background: AVAIL_COLOR[k], marginRight: 3, verticalAlign: -1 }} />{AVAIL_LABEL[k]}</span>)}
          </div>
        </div>
      )}
      {slots && (
        <div>
          <div style={{ fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6 }}>
            Suggested times (weekdays 8 AM – 6 PM, everyone free)
          </div>
          {slots.unreadable?.length > 0 && (
            <div style={{ fontSize: font.xs, color: B.amber, marginBottom: 6 }}>
              Not counted — free/busy unavailable for {slots.unreadable.join(', ')}.
            </div>
          )}
          {slots.slots.length === 0 ? (
            <div style={{ fontSize: font.sm, color: B.textLt }}>No common free time in the next 7 days.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {slots.slots.map((sl) => (
                <Chip key={sl.start} onClick={() => onPick(sl)}>
                  {fmt.date(sl.start.slice(0, 10))} {localTime12(sl.start)}
                </Chip>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Event modal (view / create / edit / respond / cancel / forward) ─────────

const RECUR_LABELS = { daily: 'Day', weekly: 'Week', monthly: 'Month', yearly: 'Year' };

function minutesBetween(sd, st, ed, et) {
  const a = new Date(`${sd}T${st}:00`);
  const b = new Date(`${ed}T${et}:00`);
  const m = Math.round((b - a) / 60000);
  return Number.isFinite(m) && m > 0 ? m : 30;
}

const EventModal = ({ eventId, seed, calendars, onClose, onChanged, onOpenId }) => {
  const { toast } = useToast();
  const isNew = !eventId;
  const [loading, setLoading] = useState(!isNew);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const seedDate = seed?.date ? isoDate(seed.date) : isoDate(new Date());
  const seedStart = seed?.time || '09:00';
  const seedEnd = (() => {
    const [h, m] = seedStart.split(':').map(Number);
    const t = h * 60 + m + 30;
    return `${pad2(Math.min(23, Math.floor(t / 60)))}:${pad2(t >= 1440 ? 59 : t % 60)}`;
  })();

  const [subject, setSubject] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [startDate, setStartDate] = useState(seedDate);
  const [startTime, setStartTime] = useState(seedStart);
  const [endDate, setEndDate] = useState(seedDate);
  const [endTime, setEndTime] = useState(seedEnd);
  const [timeZone, setTimeZone] = useState(tzNow());
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [attendees, setAttendees] = useState('');
  const [online, setOnline] = useState(false);
  const [calendarId, setCalendarId] = useState(seed?.calendarId || (calendars.find((c) => c.isDefault) || calendars[0] || {}).id || '');
  const [showAs, setShowAs] = useState('busy');
  const [isPrivate, setIsPrivate] = useState(false);
  const [reminder, setReminder] = useState('15');
  const [categories, setCategories] = useState('');
  const [allowProposals, setAllowProposals] = useState(true);

  const [repeat, setRepeat] = useState('none');
  const [interval, setInterval_] = useState(1);
  const [weekDays, setWeekDays] = useState([]);
  const [endType, setEndType] = useState('noEnd');
  const [recurEndDate, setRecurEndDate] = useState('');
  const [occurrences, setOccurrences] = useState(10);

  const [assistantOpen, setAssistantOpen] = useState(false);
  const [panel, setPanel] = useState(null); // 'respond' | 'propose' | 'cancel' | 'forward' | null
  const [pendingResponse, setPendingResponse] = useState('accept');
  const [comment, setComment] = useState('');
  const [sendResponse, setSendResponse] = useState(true);
  const [forwardTo, setForwardTo] = useState('');
  const [propStartDate, setPropStartDate] = useState(seedDate);
  const [propStartTime, setPropStartTime] = useState(seedStart);
  const [propEndTime, setPropEndTime] = useState(seedEnd);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`calendar/events/${encodeURIComponent(eventId)}?timeZone=${encodeURIComponent(tzNow())}`)
      .then((d) => {
        setDetail(d);
        setSubject(d.subject);
        setIsAllDay(d.isAllDay);
        const s = parseWallClock(d.start?.dateTime);
        const e = parseWallClock(d.end?.dateTime);
        if (s) {
          const sd = `${s.year}-${pad2(s.month)}-${pad2(s.day)}`;
          setStartDate(sd); setStartTime(`${pad2(s.hour)}:${pad2(s.minute)}`);
          setPropStartDate(sd); setPropStartTime(`${pad2(s.hour)}:${pad2(s.minute)}`);
        }
        if (e) {
          // All-day end is exclusive in Graph; show the last covered day.
          let ed = `${e.year}-${pad2(e.month)}-${pad2(e.day)}`;
          if (d.isAllDay) ed = isoDate(addDays(new Date(e.year, e.month - 1, e.day), -1));
          setEndDate(ed); setEndTime(`${pad2(e.hour)}:${pad2(e.minute)}`);
          setPropEndTime(`${pad2(e.hour)}:${pad2(e.minute)}`);
        }
        setTimeZone(d.start?.timeZone || tzNow());
        setLocation(d.location || '');
        setDescription(d.body || '');
        setAttendees(addressLine(d.attendees));
        setOnline(d.isOnlineMeeting);
        setShowAs(d.showAs || 'busy');
        setIsPrivate(d.sensitivity === 'private');
        setReminder(d.reminderMinutes == null ? '' : String(d.reminderMinutes));
        setCategories((d.categories || []).join(', '));
        setAllowProposals(d.allowNewTimeProposals !== false);
      })
      .catch((e) => toast.error(e.message || 'Could not load that event.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const canEdit = isNew || detail?.isOrganizer;
  const isInvite = !isNew && detail && !detail.isOrganizer;
  const hasAttendees = !!(detail?.attendees?.length);

  const buildPayload = () => {
    const payload = {
      subject,
      isAllDay,
      start: isAllDay ? startDate : `${startDate}T${startTime}`,
      end: isAllDay ? endDate : `${endDate}T${endTime}`,
      timeZone: isAllDay ? undefined : (timeZone || undefined),
      location,
      body: description,
      attendees: attendees.split(',').map((a) => a.trim()).filter(Boolean),
      isOnlineMeeting: online,
      showAs,
      sensitivity: isPrivate ? 'private' : 'normal',
      reminderMinutes: reminder === '' ? null : Number(reminder),
      categories: categories.split(',').map((c) => c.trim()).filter(Boolean),
      allowNewTimeProposals: allowProposals,
    };
    if (isNew && calendarId) payload.calendarId = calendarId;
    if (isNew && repeat !== 'none') {
      payload.recurrence = {
        freq: repeat, interval: Number(interval) || 1, daysOfWeek: weekDays,
        endType, endDate: recurEndDate, occurrences: Number(occurrences) || 1,
      };
    }
    return payload;
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = buildPayload();
      if (isNew) await api.post('calendar/events', payload);
      else await api.put(`calendar/events/${encodeURIComponent(eventId)}`, payload);
      toast.success(isNew ? (payload.attendees.length ? 'Invitation sent' : 'Event created') : 'Event updated');
      onChanged();
    } catch (e) { toast.error(e.message || 'Could not save that event.'); }
    finally { setBusy(false); }
  };

  // An invite you only attend is a "Remove"; your own meeting WITH attendees
  // opens the Cancel panel (so they get a note); a private appointment is a
  // plain delete.
  const del = async () => {
    if (detail?.isOrganizer && hasAttendees) { setPanel('cancel'); return; }
    if (!window.confirm(detail?.isOrganizer ? 'Delete this event?' : 'Decline and remove this event from your calendar?')) return;
    setBusy(true);
    try {
      await api.del(`calendar/events/${encodeURIComponent(eventId)}`);
      toast.success('Removed');
      onChanged();
    } catch (e) { toast.error(e.message || 'Could not remove that event.'); }
    finally { setBusy(false); }
  };

  const submitPanel = async () => {
    setBusy(true);
    try {
      const idPath = `calendar/events/${encodeURIComponent(eventId)}`;
      if (panel === 'respond') {
        await api.post(`${idPath}/respond`, { response: pendingResponse, comment, sendResponse });
        toast.success(sendResponse ? 'Response sent' : 'Response saved');
      } else if (panel === 'propose') {
        await api.post(`${idPath}/respond`, {
          response: pendingResponse, comment, sendResponse: true,
          proposedNewTime: { start: `${propStartDate}T${propStartTime}`, end: `${propStartDate}T${propEndTime}`, timeZone: tzNow() },
        });
        toast.success('New time proposed to the organizer');
      } else if (panel === 'cancel') {
        await api.post(`${idPath}/cancel`, { comment });
        toast.success('Meeting cancelled — attendees were notified');
      } else if (panel === 'forward') {
        await api.post(`${idPath}/forward`, { to: forwardTo, comment });
        toast.success('Invitation forwarded');
        setPanel(null); setBusy(false);
        return;
      }
      onChanged();
    } catch (e) { toast.error(e.message || 'That did not go through.'); }
    finally { setBusy(false); }
  };

  const openRespond = (response) => { setPendingResponse(response); setComment(''); setPanel('respond'); };
  const toggleWeekDay = (d) => setWeekDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  if (loading) {
    return <Modal title="Event" onClose={onClose}><Spinner label="Loading…" /></Modal>;
  }

  const writableCalendars = calendars.filter((c) => c.canEdit);

  return (
    <Modal title={isNew ? 'New event' : (canEdit ? 'Edit event' : 'Event')} onClose={onClose} width={680}
      headerAction={!isNew && detail?.webLink ? (
        <Btn size="sm" variant="ghost" onClick={() => window.open(detail.webLink, '_blank', 'noopener')} title="Open in Outlook on the web"><Icon name="external" size={13} /></Btn>
      ) : null}>
      {!isNew && detail?.type !== 'singleInstance' && (
        <div style={{ fontSize: font.xs, color: B.textMd, marginBottom: space.md, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="refresh" size={12} /> Part of a repeating series — saving here changes only this occurrence.
          {detail?.seriesMasterId && detail.seriesMasterId !== eventId && (
            <button onClick={() => onOpenId(detail.seriesMasterId)} style={{ background: 'none', border: 'none', color: B.navy, cursor: 'pointer', textDecoration: 'underline', fontSize: font.xs, fontFamily: 'inherit', padding: 0 }}>
              Edit the whole series instead
            </button>
          )}
        </div>
      )}

      {isInvite && (
        <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.md }}>
          Organized by <strong>{detail.organizer?.name || detail.organizer?.address}</strong> — you were invited.
          {detail.myResponse && detail.myResponse !== 'none' && <> Your response: <strong>{RESPONSE_LABEL[detail.myResponse] || detail.myResponse}</strong>.</>}
        </div>
      )}

      {detail?.joinUrl && (
        <div style={{ marginBottom: space.md }}>
          <Btn size="sm" onClick={() => window.open(detail.joinUrl, '_blank', 'noopener')}><Icon name="video" size={13} /> Join Teams meeting</Btn>
        </div>
      )}

      {isNew && writableCalendars.length > 1 && (
        <Select label="Calendar" value={calendarId} onChange={setCalendarId}
          options={writableCalendars.map((c) => ({ value: c.id, label: c.isDefault ? `${c.name} (default)` : c.name }))} />
      )}

      <Input label="Subject" value={subject} onChange={setSubject} disabled={!canEdit} autoFocus={isNew} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: space.md }}>
        <input id="allday" type="checkbox" checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} disabled={!canEdit} />
        <label htmlFor="allday" style={{ fontSize: font.sm, color: B.text }}>All day</label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isAllDay ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: space.md }}>
        <Input label="Start" type="date" value={startDate} onChange={(v) => { setStartDate(v); if (endDate < v) setEndDate(v); }} disabled={!canEdit} />
        {!isAllDay && <Input label="Start time" type="time" value={startTime} onChange={setStartTime} disabled={!canEdit} />}
        <Input label="End" type="date" value={endDate} onChange={setEndDate} disabled={!canEdit} />
        {!isAllDay && <Input label="End time" type="time" value={endTime} onChange={setEndTime} disabled={!canEdit} />}
      </div>
      {!isAllDay && canEdit && <Input label="Timezone" value={timeZone} onChange={setTimeZone} placeholder="America/Chicago" />}

      <Input label="Location" value={location} onChange={setLocation} disabled={!canEdit} />
      <Input label="Attendees (comma separated)" value={attendees} onChange={setAttendees} disabled={!canEdit} />

      {!isNew && hasAttendees && (
        <div style={{ marginBottom: space.md, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {detail.attendees.map((a) => (
            <Chip key={a.address} color={a.response === 'accepted' ? B.green : a.response === 'declined' ? B.red : a.response === 'tentativelyAccepted' ? B.amber : B.textMd}>
              {a.name || a.address}{a.type === 'optional' ? ' (optional)' : ''} · {RESPONSE_LABEL[a.response] || 'No response'}
            </Chip>
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ marginBottom: space.md }}>
          <Btn size="sm" variant="ghost" onClick={() => setAssistantOpen((v) => !v)}>
            <Icon name="calendar" size={12} /> {assistantOpen ? 'Hide scheduling assistant' : 'Scheduling assistant'}
          </Btn>
        </div>
      )}
      {canEdit && assistantOpen && (
        <SchedulingAssistant attendees={attendees} date={startDate}
          durationMinutes={isAllDay ? 60 : minutesBetween(startDate, startTime, endDate, endTime)}
          onPick={(sl) => {
            setIsAllDay(false);
            setStartDate(sl.start.slice(0, 10)); setStartTime(sl.start.slice(11, 16));
            setEndDate(sl.end.slice(0, 10)); setEndTime(sl.end.slice(11, 16));
            toast.success(`Moved to ${fmt.date(sl.start.slice(0, 10))} ${localTime12(sl.start)}`);
          }} />
      )}

      <Textarea label="Description" value={description} onChange={setDescription} rows={4} disabled={!canEdit} />

      {canEdit && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
          <Select label="Show as" value={showAs} onChange={setShowAs} options={SHOW_AS_OPTIONS} />
          <Select label="Reminder" value={reminder} onChange={setReminder} options={REMINDER_CHOICES} />
        </div>
      )}
      {canEdit && <Input label="Categories (comma separated)" value={categories} onChange={setCategories} placeholder="e.g. Red category, Deals" />}

      {canEdit && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.lg, alignItems: 'center', marginBottom: space.lg, fontSize: font.sm, color: B.text }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} />
            <Icon name="video" size={12} /> Teams meeting
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
            <Icon name="lock" size={12} /> Private
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={allowProposals} onChange={(e) => setAllowProposals(e.target.checked)} /> Allow new time proposals
          </label>
        </div>
      )}

      {isNew && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: space.md, marginBottom: space.lg }}>
          <Select label="Repeat" value={repeat} onChange={setRepeat}
            options={[{ value: 'none', label: 'Does not repeat' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }]} />
          {repeat !== 'none' && (
            <>
              <Input label={`Every N ${RECUR_LABELS[repeat]?.toLowerCase()}(s)`} type="number" value={String(interval)} onChange={setInterval_} />
              {repeat === 'weekly' && (
                <div style={{ marginBottom: space.md }}>
                  <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: 6 }}>Repeat on</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {WEEKDAY_KEYS.map((d, i) => (
                      <button key={d} type="button" onClick={() => toggleWeekDay(d)}
                        style={{
                          padding: '5px 10px', borderRadius: radius.pill, cursor: 'pointer', fontSize: 12,
                          border: `1px solid ${weekDays.includes(d) ? B.navy : B.border}`,
                          background: weekDays.includes(d) ? B.tealLt : B.white, color: B.text, fontFamily: 'inherit',
                        }}>{WEEKDAY_LABELS[i]}</button>
                    ))}
                  </div>
                </div>
              )}
              <Select label="Ends" value={endType} onChange={setEndType}
                options={[{ value: 'noEnd', label: 'Never' }, { value: 'onDate', label: 'On a date' }, { value: 'afterCount', label: 'After a number of times' }]} />
              {endType === 'onDate' && <Input label="End date" type="date" value={recurEndDate} onChange={setRecurEndDate} />}
              {endType === 'afterCount' && <Input label="Number of occurrences" type="number" value={String(occurrences)} onChange={setOccurrences} />}
            </>
          )}
        </div>
      )}

      {isInvite && !panel && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: space.lg }}>
          <Btn size="sm" onClick={() => openRespond('accept')} disabled={busy}>Accept</Btn>
          <Btn size="sm" variant="secondary" onClick={() => openRespond('tentativelyAccept')} disabled={busy}>Tentative</Btn>
          <Btn size="sm" variant="ghost" onClick={() => openRespond('decline')} disabled={busy}>Decline</Btn>
          {detail.allowNewTimeProposals && (
            <Btn size="sm" variant="ghost" onClick={() => { setPendingResponse('tentativelyAccept'); setComment(''); setPanel('propose'); }} disabled={busy}>
              <Icon name="clock" size={12} /> Propose new time
            </Btn>
          )}
        </div>
      )}

      {panel && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: space.md, marginBottom: space.lg, background: B.surfaceMuted }}>
          <div style={{ fontSize: font.sm, fontWeight: weight.semibold, marginBottom: space.sm }}>
            {panel === 'respond' && `${pendingResponse === 'accept' ? 'Accept' : pendingResponse === 'decline' ? 'Decline' : 'Tentatively accept'} this invitation`}
            {panel === 'propose' && 'Propose a new time to the organizer'}
            {panel === 'cancel' && 'Cancel this meeting — attendees get your note'}
            {panel === 'forward' && 'Forward this invitation'}
          </div>
          {panel === 'propose' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: space.md }}>
                <Input label="Date" type="date" value={propStartDate} onChange={setPropStartDate} />
                <Input label="Start" type="time" value={propStartTime} onChange={setPropStartTime} />
                <Input label="End" type="time" value={propEndTime} onChange={setPropEndTime} />
              </div>
              <Select label="And meanwhile" value={pendingResponse} onChange={setPendingResponse}
                options={[{ value: 'tentativelyAccept', label: 'Tentatively accept' }, { value: 'decline', label: 'Decline' }]} />
            </>
          )}
          {panel === 'forward' && <Input label="To (comma separated)" value={forwardTo} onChange={setForwardTo} placeholder="name@company.com" />}
          <Textarea label="Note (optional)" value={comment} onChange={setComment} rows={2} />
          {panel === 'respond' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: font.sm, marginBottom: space.sm }}>
              <input type="checkbox" checked={sendResponse} onChange={(e) => setSendResponse(e.target.checked)} /> Send a response to the organizer
            </label>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn size="sm" variant="ghost" onClick={() => setPanel(null)} disabled={busy}>Back</Btn>
            <Btn size="sm" onClick={submitPanel} loading={busy} disabled={panel === 'forward' && !forwardTo.trim()}>
              {panel === 'cancel' ? 'Cancel meeting' : panel === 'forward' ? 'Forward' : 'Send'}
            </Btn>
          </div>
        </div>
      )}

      <ModalFooter>
        {!isNew && (
          <Btn variant="ghost" onClick={del} disabled={busy}>
            <Icon name="trash" size={13} /> {detail?.isOrganizer ? (hasAttendees ? 'Cancel meeting' : 'Delete') : 'Remove from my calendar'}
          </Btn>
        )}
        {!isNew && <Btn variant="ghost" onClick={() => { setComment(''); setPanel('forward'); }} disabled={busy}><Icon name="forward" size={13} /> Forward</Btn>}
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
        {canEdit && <Btn onClick={save} loading={busy}>{isNew ? (attendees.trim() ? 'Send' : 'Create') : (hasAttendees ? 'Send update' : 'Save')}</Btn>}
      </ModalFooter>
    </Modal>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const VIEWS = [['day', 'Day'], ['workweek', 'Work week'], ['week', 'Week'], ['month', 'Month'], ['agenda', 'Agenda']];

export const CalendarPage = ({ setPage }) => {
  const { toast } = useToast();
  const { data: status, loading: statusLoading } = usePersonalConnectStatus();
  const connected = !!status?.connected;

  const [prefs] = useState(loadCalPrefs);
  const [view, setView] = useState(VIEWS.some(([k]) => k === prefs.view) ? prefs.view : 'week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // { eventId } | { seed: { date, time?, calendarId? } } | null
  const [calendars, setCalendars] = useState([]);
  const [calendarsLoaded, setCalendarsLoaded] = useState(false);
  const [hidden, setHidden] = useState(() => new Set(prefs.hidden));

  useEffect(() => { saveCalPrefs({ hidden: [...hidden], view }); }, [hidden, view]);

  useEffect(() => {
    if (!connected) return;
    api.get('calendar/calendars')
      .then((r) => setCalendars(r.calendars || []))
      .catch(() => setCalendars([])) // one default calendar still works without the list
      .finally(() => setCalendarsLoaded(true));
  }, [connected]);

  const range = useMemo(() => viewRange(view, anchor), [view, anchor]);
  const visibleCalendars = useMemo(() => calendars.filter((c) => !hidden.has(c.id)), [calendars, hidden]);

  const load = useCallback(async () => {
    if (!calendarsLoaded) return;
    setLoading(true);
    try {
      const base = {
        start: isoDateTimeMidnight(range.start),
        end: isoDateTimeMidnight(range.end),
        top: '250',
        timeZone: tzNow(),
      };
      // No calendar list (an older API, or the read failed) → the default
      // calendar alone, exactly as before multiple calendars existed.
      const targets = calendars.length ? visibleCalendars : [{ id: null, color: DEFAULT_COLOR }];
      const results = await Promise.all(targets.map(async (c) => {
        const params = new URLSearchParams(base);
        if (c.id) params.set('calendarId', c.id);
        try {
          const r = await api.get(`calendar/events?${params.toString()}`);
          return (r.events || []).map((e) => ({ ...e, calendarId: c.id, color: c.color || DEFAULT_COLOR }));
        } catch (e) {
          toast.error(`${c.name || 'Calendar'}: ${e.message || 'could not load'}`);
          return [];
        }
      }));
      // A DEFENSIVE FLOOR, not a replacement for the server's own window: an
      // event whose own end is before the window opened has plainly already
      // happened, so dropping it can never hide a real in-window event.
      const floorKey = isoDate(range.start);
      setEvents(results.flat().filter((e) => {
        const endKey = dayKeyOf(e.end || e.start);
        return !endKey || endKey >= floorKey;
      }));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start.getTime(), range.end.getTime(), calendarsLoaded, visibleCalendars.map((c) => c.id).join('|')]);

  useEffect(() => { if (connected) load(); }, [connected, load]);

  const days = useMemo(() => {
    if (view === 'day' || view === 'week' || view === 'workweek') {
      const n = Math.round((range.end - range.start) / 86400000);
      return Array.from({ length: n }, (_, i) => addDays(range.start, i));
    }
    return [];
  }, [view, range]);

  const opts = { month: 'short', day: 'numeric' };
  const last = addDays(range.end, -1);
  const rangeLabel = view === 'day'
    ? anchor.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : view === 'month'
      ? anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : `${range.start.toLocaleDateString('en-US', opts)} – ${last.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;

  const openEvent = (e) => setModal({ eventId: e.id });
  const newAt = (date, hour, minute) => setModal({ seed: { date, time: `${pad2(hour)}:${pad2(minute)}` } });
  const toggleCalendar = (id) => setHidden((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  if (statusLoading) return <Spinner label="Loading Calendar…" />;
  if (!connected) return <NotConnectedCard feature="Calendar" status={status} setPage={setPage} />;

  const viewLabel = (VIEWS.find(([k]) => k === view) || VIEWS[2])[1];

  return (
    <div>
      <PageHeader title="Calendar" icon="calendar" actions={
        <>
          <Btn variant="ghost" onClick={load} loading={loading} title="Re-read your calendar from Outlook now"><Icon name="refresh" size={14} /> Sync</Btn>
          <Btn onClick={() => setModal({ seed: { date: new Date() } })}><Icon name="add" size={14} /> New event</Btn>
        </>
      } />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.md, flexWrap: 'wrap', gap: space.md }}>
        <Tabs tabs={VIEWS.map(([, l]) => l)} active={viewLabel} onChange={(t) => setView((VIEWS.find(([, l]) => l === t) || VIEWS[2])[0])} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={() => setAnchor((d) => stepAnchor(view, d, -1))} title="Previous"><Icon name="chevronLeft" size={14} /></Btn>
          <Btn size="sm" variant="ghost" onClick={() => setAnchor(new Date())}>Today</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setAnchor((d) => stepAnchor(view, d, 1))} title="Next"><Icon name="chevronRight" size={14} /></Btn>
          <input type="date" aria-label="Go to date" value={isoDate(anchor)}
            onChange={(e) => { const [y, m, d] = e.target.value.split('-').map(Number); if (y) setAnchor(new Date(y, m - 1, d)); }}
            style={{ fontFamily: 'inherit', fontSize: font.sm, padding: '4px 6px', borderRadius: radius.md, border: `1px solid ${B.border}` }} />
          <span style={{ fontSize: font.sm, color: B.textMd, fontWeight: weight.medium }}>{rangeLabel}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: space.lg, alignItems: 'flex-start' }}>
        {calendars.length > 0 && (
          <div style={{ width: 190, flexShrink: 0 }}>
            <div style={{ fontSize: font.xs, fontWeight: weight.semibold, color: B.textMd, textTransform: 'uppercase', marginBottom: 6 }}>My calendars</div>
            {calendars.map((c) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: font.sm, cursor: 'pointer' }} title={c.owner?.address || c.name}>
                <input type="checkbox" checked={!hidden.has(c.id)} onChange={() => toggleCalendar(c.id)} style={{ accentColor: c.color || DEFAULT_COLOR }} />
                <span style={{ width: 10, height: 10, borderRadius: 3, background: c.color || DEFAULT_COLOR, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {loading && events.length === 0 && <Spinner label="Loading events…" />}
          {(!loading || events.length > 0) && days.length > 0 && <TimeGrid days={days} events={events} onOpen={openEvent} onNewAt={newAt} />}
          {(!loading || events.length > 0) && view === 'month' && (
            <MonthView anchor={anchor} events={events} onOpen={openEvent}
              onNewOn={(d) => setModal({ seed: { date: d } })}
              onDrill={(d) => { setAnchor(d); setView('day'); }} />
          )}
          {(!loading || events.length > 0) && view === 'agenda' && <AgendaView events={events} onOpen={openEvent} />}
        </div>
      </div>

      {modal && (
        <EventModal
          key={modal.eventId || 'new'}
          eventId={modal.eventId}
          seed={modal.seed}
          calendars={calendars}
          onClose={() => setModal(null)}
          onOpenId={(id) => setModal({ eventId: id })}
          onChanged={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
};
